import { useEffect, useRef, type ReactNode } from 'react'

/**
 * A bottom sheet on a phone, a centered dialog on a desktop.
 *
 * One component rather than two because the behaviour a dialog owes the user is
 * identical either way: the page behind it stops scrolling, focus cannot leave,
 * Escape and the backdrop both close it, and focus returns to whatever opened
 * it. Only the position differs, and that is a media query.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Which sheets are open, innermost last.
 *
 * A block editor opens over a week, which is open over the cycle. Without this,
 * every one of them would hear the same Escape and the whole stack would
 * vanish at once — so only the top of the stack acts on it.
 */
const openSheets: symbol[] = []

export function Sheet({
  open,
  onClose,
  title,
  eyebrow,
  children,
  footer,
  wide,
}: {
  open: boolean
  onClose: () => void
  title: string
  eyebrow?: string
  children: ReactNode
  footer?: ReactNode
  /** For content that needs the room — a week of days, a printable book. */
  wide?: boolean
}) {
  const panel = useRef<HTMLDivElement>(null)
  const returnTo = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return

    const me = Symbol('sheet')
    openSheets.push(me)
    const isTop = () => openSheets[openSheets.length - 1] === me

    returnTo.current = document.activeElement as HTMLElement | null

    // Scroll lock. Padding compensates for the scrollbar so the page behind
    // does not visibly jump on desktop.
    const { overflow, paddingRight } = document.body.style
    const gap = window.innerWidth - document.documentElement.clientWidth
    document.body.style.overflow = 'hidden'
    if (gap > 0) document.body.style.paddingRight = `${gap}px`

    const first = panel.current?.querySelector<HTMLElement>(FOCUSABLE)
    ;(first ?? panel.current)?.focus()

    const onKey = (e: KeyboardEvent) => {
      if (!isTop()) return
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab' || !panel.current) return

      const items = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null,
      )
      if (!items.length) return
      const first = items[0]!
      const last = items[items.length - 1]!
      const active = document.activeElement

      if (e.shiftKey && (active === first || active === panel.current)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      const at = openSheets.indexOf(me)
      if (at >= 0) openSheets.splice(at, 1)
      document.body.style.overflow = overflow
      document.body.style.paddingRight = paddingRight
      returnTo.current?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="sheet-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={panel}
        className={`sheet${wide ? ' is-wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <header className="sheet-head">
          <span className="sheet-grip" aria-hidden="true" />
          <div className="sheet-head-text">
            {eyebrow && <div className="sheet-eyebrow">{eyebrow}</div>}
            <h2 className="sheet-title">{title}</h2>
          </div>
          <button className="sheet-close" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </header>

        <div className="sheet-body">{children}</div>

        {footer && <footer className="sheet-foot">{footer}</footer>}
      </div>
    </div>
  )
}

const CloseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
)
